import React, { useState } from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import { useShiftKey } from '@/hooks/useShiftKey';

export interface ShiftActionOverlayProps {
  children: React.ReactNode;
  onAprobar?: () => void | Promise<void>;
  onRechazar?: () => void | Promise<void>;
  disabled?: boolean;
  className?: string;
}

export function ShiftActionOverlay({ children, onAprobar, onRechazar, disabled = false, className = '' }: ShiftActionOverlayProps) {
  const isShiftPressed = useShiftKey();
  const [isHovered, setIsHovered] = useState(false);
  const [busy, setBusy] = useState<'aprobar' | 'rechazar' | null>(null);

  const showOverlay = isShiftPressed && isHovered && !disabled && (!!onAprobar || !!onRechazar);

  const handleAprobarClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!onAprobar || busy !== null) return;
    setBusy('aprobar');
    try {
      await onAprobar();
    } finally {
      setBusy(null);
    }
  };

  const handleRechazarClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!onRechazar || busy !== null) return;
    setBusy('rechazar');
    try {
      await onRechazar();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className={`relative group ${isShiftPressed ? 'select-none' : ''} ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}

      {showOverlay && (
        <div
          className={`absolute inset-0 z-30 flex rounded-xl overflow-hidden shadow-lg backdrop-blur-md transition-all duration-300 ease-in-out animate-in fade-in-0 ${
            busy ? 'opacity-80 scale-[0.98]' : 'opacity-100'
          }`}
        >
          {/* Left half: Rechazar (80% opacidad) */}
          {onRechazar ? (
            <button
              type="button"
              onClick={handleRechazarClick}
              disabled={busy !== null}
              className="flex-1 bg-red-600/80 hover:bg-red-600/90 active:opacity-80 active:scale-95 text-white flex flex-col items-center justify-center p-2 transition-all duration-200 cursor-pointer group/btn"
            >
              <XCircle className="w-5 h-5 mb-1 text-white group-hover/btn:scale-110 transition-transform" />
              <span className="text-[11px] font-bold uppercase tracking-wider">{busy === 'rechazar' ? 'Rechazando...' : 'Rechazar'}</span>
            </button>
          ) : (
            <div className="flex-1 bg-gray-500/20" />
          )}

          {/* Right half: Aprobar (80% opacidad) */}
          {onAprobar ? (
            <button
              type="button"
              onClick={handleAprobarClick}
              disabled={busy !== null}
              className="flex-1 bg-emerald-600/80 hover:bg-emerald-600/90 active:opacity-80 active:scale-95 text-white flex flex-col items-center justify-center p-2 transition-all duration-200 cursor-pointer group/btn"
            >
              <CheckCircle className="w-5 h-5 mb-1 text-white group-hover/btn:scale-110 transition-transform" />
              <span className="text-[11px] font-bold uppercase tracking-wider">{busy === 'aprobar' ? 'Aprobando...' : 'Aprobar'}</span>
            </button>
          ) : (
            <div className="flex-1 bg-gray-500/20" />
          )}
        </div>
      )}
    </div>
  );
}
